import { action, observable } from 'mobx';
import { provideSingleton, inject, bind } from '../utils/decorators';
import { IObjektVorlage, IObjekt, IArbeitsmittel } from '../models/ApiTypes';
import { AuftragStore } from './AuftragStore';
import KalibrierGegenstand from '../models/KalibrierGegenstand';
import { round } from '../utils/round';
import { IBpsReferenzkraftMesswerte, IBpsReferenzkraftBeurteilungRequest, IBpsReferenzkraftBeurteilungPunkt } from '../models/ApiTypesBps';
import { ApiStore } from './ApiStore';
import { VorgangStore } from './VorgangStore';

export type BpsOberflaechenTyp = 'beschichtet' | 'geschweisst';
export type BpsRolleMessArt = 'durchmesser' | 'umfang';
export type BpsMessBereich = 'klein' | 'groß' | 'kombiniert' | undefined;
export type BpsMessBereichReichweite = number | undefined;
export type BpsPositionRolle = 'links' | 'rechts';
export type BpsMesswertAnzeige = 'analog' | 'digital' | undefined;

/**
 * Interfaces für Prüfortparameter
 */
export interface IBpsReferenzkraftPruefortParameterKomponente {
  wert: number | null;
  min: number;
  max: number;
}

export interface IBpsReferenzkraftPruefortParameter {
  lufttemperatur: IBpsReferenzkraftPruefortParameterKomponente;
  luftfeuchtigkeit: IBpsReferenzkraftPruefortParameterKomponente;
}

/**
 * Interfaces mit allen Daten zu einer Rollensatz-Vorbelastung
 */
export interface IBpsReferenzkraftRollensatzVorbelastungMesspunkt {
  anzeige: number | null;
  normal: number | null;
}

export interface IBpsReferenzkraftRollensatzVorbelastung {
  messpunkte: IBpsReferenzkraftRollensatzVorbelastungMesspunkt[];
}

/**
 * Interface mit allen Daten zu einem Rollensatz-Messpunkt
 */
export interface IBpsReferenzkraftRollensatzMesspunkt {
  anzeige: number;
  normal: number;
  messwerte: Array<number | null>;
  mittelwert: number | undefined;
  mittelwertabweichung: number | null;
  messunsicherheit: number | null;
  fehlergrenze: number;
  konformitaet: string | null;
}

/**
 * Interface mit allen Daten zu einem Rollensatz
 */
export interface IBpsReferenzkraftRollensatz {
  position: BpsPositionRolle;
  messpunkte: IBpsReferenzkraftRollensatzMesspunkt[];
  vorbelastung: IBpsReferenzkraftRollensatzVorbelastung;
}

/**
 * Interface mit allen Daten zum Rollendurchmesser
 */
export interface IBpsReferenzkraftRollendurchmesser {
  messwerte: Array<Array<number | undefined>>;
  mittelwert: {
    links: number | undefined;
    rechts: number | undefined;
  };
}

/**
 * Interface für Messung Wägezelle
 */
export interface IBpsReferenzkraftWaegezelle {
  messwerte: Array<number | undefined>;
  sollwert: number;
  toleranz: number;
}

/**
 * Interfaces für Ermittlung Rolllendrehzahl und Abschaltschlupf via ASA-Livestream
 */
export interface IBpsReferenzkraftDrehzahlSchlupfRollensatz {
  geschwindigkeit: Array<number | undefined>;
  schlupf: number | null;
}

export interface IBpsReferenzkraftDrehzahlSchlupf {
  links: IBpsReferenzkraftDrehzahlSchlupfRollensatz;
  rechts: IBpsReferenzkraftDrehzahlSchlupfRollensatz;
  mingeschwindigkeit: number;
  maxschlupf: number;
}

/**
 * Interface mit allen Daten zur Messwerteingabe- und Berechnung für die Beurteilung der BPS Rolle
 */
export interface IBpsReferenzkraftRolleMessung {
  version: number | null;
  rollendurchmesser: IBpsReferenzkraftRollendurchmesser;
  oberflaeche: BpsOberflaechenTyp;
  messart: BpsRolleMessArt;
  messbereich: BpsMessBereich;
  messbereichKlein: BpsMessBereichReichweite;
  messbereichGross: BpsMessBereichReichweite;
  anzeige: BpsMesswertAnzeige;
  rollensatz: {
    // @todo: Vor/Nach Justage
    links: IBpsReferenzkraftRollensatz;
    rechts: IBpsReferenzkraftRollensatz;
  };
  pruefortparameter: {
    beginn: IBpsReferenzkraftPruefortParameter;
    ende: IBpsReferenzkraftPruefortParameter;
  };
  waegezelle: IBpsReferenzkraftWaegezelle;
  drehzahlschlupf: IBpsReferenzkraftDrehzahlSchlupf;
}

/**
 * Singleton für globale Eigenschaften zur BPS Kalibrierung mit Referenzkraft-Verfahren
 */
@provideSingleton(BpsReferenzkraftKalibrierungStore)
export class BpsReferenzkraftKalibrierungStore {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(AuftragStore) auftragStore: AuftragStore;
  @inject(VorgangStore) vorgangStore: VorgangStore;

  @observable objektVorlage: IObjektVorlage | undefined = undefined;
  @observable kalibrierGegenstand: KalibrierGegenstand | undefined = undefined;
  @observable justage: boolean = false;
  @observable readOnly: boolean = false;

  // @todo: Naming
  private muAbrufTimeoutRef: number = 0;

  @observable datensatz: IBpsReferenzkraftRolleMessung = {
    version: 1,
    rollendurchmesser: {
      messwerte: [[undefined, undefined, undefined], [undefined, undefined, undefined], [undefined, undefined, undefined], [undefined, undefined, undefined]],
      mittelwert: {
        links: undefined,
        rechts: undefined
      }
    },
    oberflaeche: 'beschichtet',
    messart: 'durchmesser',
    messbereich: undefined,
    messbereichKlein: undefined,
    messbereichGross: undefined,
    anzeige: undefined,
    rollensatz: {
      links: {
        position: 'links',
        messpunkte: [],
        vorbelastung: {
          messpunkte: [
            { anzeige: null, normal: null },
            { anzeige: null, normal: null }
          ]
        }
      },
      rechts: {
        position: 'rechts',
        messpunkte: [],
        vorbelastung: {
          messpunkte: [
            { anzeige: null, normal: null },
            { anzeige: null, normal: null }
          ]
        }
      }
    },
    pruefortparameter: {
      beginn: {
        luftfeuchtigkeit: {
          wert: null,
          min: 0,
          max: 85
        },
        lufttemperatur: {
          wert: null,
          min: 5,
          max: 35
        }
      },
      ende: {
        luftfeuchtigkeit: {
          wert: null,
          min: 0,
          max: 85
        },
        lufttemperatur: {
          wert: null,
          min: 5,
          max: 35
        }
      }
    },
    waegezelle: {
      messwerte: [undefined, undefined, undefined],
      sollwert: 49,
      toleranz: 1
    },
    drehzahlschlupf: {
      links: {
        geschwindigkeit: [undefined, undefined],
        schlupf: null
      },
      rechts: {
        geschwindigkeit: [undefined, undefined],
        schlupf: null
      },
      mingeschwindigkeit: 2,
      maxschlupf: 30
    }
  };

  messbereiche: Array<number> = [4, 5 , 6, 8, 12, 25, 30, 40];

  @action clear() {
    this.datensatz = {
      version: 1,
      rollendurchmesser: {
        messwerte: [[undefined, undefined, undefined], [undefined, undefined, undefined], [undefined, undefined, undefined], [undefined, undefined, undefined]],
        mittelwert: {
          links: undefined,
          rechts: undefined
        }
      },
      oberflaeche: 'beschichtet',
      messart: 'durchmesser',
      messbereich: undefined,
      messbereichKlein: undefined,
      messbereichGross: undefined,
      anzeige: undefined,
      rollensatz: {
        links: {
          position: 'links',
          messpunkte: [],
          vorbelastung: {
            messpunkte: [
              { anzeige: null, normal: null },
              { anzeige: null, normal: null }
            ]
          }
        },
        rechts: {
          position: 'rechts',
          messpunkte: [],
          vorbelastung: {
            messpunkte: [
              { anzeige: null, normal: null },
              { anzeige: null, normal: null }
            ]
          }
        }
      },
      pruefortparameter: {
        beginn: {
          luftfeuchtigkeit: {
            wert: null,
            min: 0,
            max: 85
          },
          lufttemperatur: {
            wert: null,
            min: 5,
            max: 35
          }
        },
        ende: {
          luftfeuchtigkeit: {
            wert: null,
            min: 0,
            max: 85
          },
          lufttemperatur: {
            wert: null,
            min: 5,
            max: 35
          }
        }
      },
      waegezelle: {
        messwerte: [undefined, undefined, undefined],
        sollwert: 49,
        toleranz: 1
      },
      drehzahlschlupf: {
        links: {
          geschwindigkeit: [undefined, undefined],
          schlupf: null
        },
        rechts: {
          geschwindigkeit: [undefined, undefined],
          schlupf: null
        },
        mingeschwindigkeit: 2,
        maxschlupf: 30
      }
    };
  }

  /**
   * Ruft mit der aktuellen Eingabe Sollwerte aus dem Back-End ab
   */
  async messpunkteAbrufen() {
    const { datensatz, kalibrierGegenstand } = this;
    if (kalibrierGegenstand && kalibrierGegenstand.objekt && datensatz.messbereich && datensatz.rollendurchmesser.mittelwert.links && datensatz.rollendurchmesser.mittelwert.rechts) {
      if ((datensatz.messbereich === 'klein' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichKlein) {
        // @todo: reset
        return;
      }
      if ((datensatz.messbereich === 'groß' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichGross) {
        // @todo: reset
        return;
      }
      let messwerte: IBpsReferenzkraftMesswerte = {
        mittelwerte: {
          links: datensatz.rollendurchmesser.mittelwert.links,
          rechts: datensatz.rollendurchmesser.mittelwert.rechts
        },
        messbereich: datensatz.messbereich,
        messbereiche: {
          klein: datensatz.messbereichKlein,
          gross: datensatz.messbereichGross
        },
        objekt: kalibrierGegenstand.objekt.objektguid
      };

      const response = await this.apiStore.bpsReferenzkraftMesspunkte(messwerte);
      if (response.ok) {
        return response;
      }
    }
    // @todo: Hinweis/Ergebnis bereinigen
    return undefined;
  }

  @bind @action setMesspunkt(ziel: IBpsReferenzkraftRollensatzMesspunkt, anzeige: number, normal: number, fehlergrenze: number) {
    if (ziel) {
      ziel.anzeige = anzeige;
      ziel.normal = normal;
      ziel.fehlergrenze = fehlergrenze;
    }
  }

  /**
   * Aktualisiert die Sollwerte für bereits angelegte Messpunkte
   */
  async messpunkteAktualisieren() {
    const response = await this.messpunkteAbrufen();
    const { datensatz } = this;
    if (response && response.ok) {
      const messpunkte = response.json;
      // @todo: check if count of measurement points align
      messpunkte.forEach((mp, index) => {
        this.setMesspunkt(datensatz.rollensatz.links.messpunkte[index], mp.anzeige, mp.normal.links, mp.fehlergrenze);
        this.setMesspunkt(datensatz.rollensatz.rechts.messpunkte[index], mp.anzeige, mp.normal.rechts, mp.fehlergrenze);
      });
    }
  }

  /** Anfang Beurteilungen abrufen */

  /**
   * @todo: document
   */
  @bind async beurteilungAbrufen(ziel: IBpsReferenzkraftRollensatz) {
    // @todo: prüfen ob daten vorhanden
    if (ziel.messpunkte.length === 0) {
      return;
    }
    const nullpunktVollstaendig = ziel.messpunkte[0].messwerte.every(wert => wert !== null);
    if (!this.kalibrierGegenstand || !this.kalibrierGegenstand.objekt) {
      return;
    }
    if (!nullpunktVollstaendig) {
      ziel.messpunkte.forEach((element, index) => {
        this.beurteilungMesspunktLoeschen(ziel, index);
      });
      return;
    }

    // Kraftmessgerät aus Liste der gewählten Arbeitsmittel
    let kmg: IArbeitsmittel | undefined = this.kraftMessGeraet();
    if (!kmg) {
      return;
    }

    let messwerte: IBpsReferenzkraftBeurteilungRequest = {
      mittelwerte: {
        links: this.datensatz.rollendurchmesser.mittelwert.links,
        rechts: this.datensatz.rollendurchmesser.mittelwert.rechts
      },
      position: ziel.position,
      messbereich: this.datensatz.messbereich,
      messbereiche: {
        klein: this.datensatz.messbereichKlein,
        gross: this.datensatz.messbereichGross
      },
      messwerte: [],
      objekt: this.kalibrierGegenstand.objekt.objektguid,
      kmg: kmg.arbeitsmittelguid
    };

    ziel.messpunkte.forEach(mp => {
      messwerte.messwerte.push(
        {
          messwerte: mp.messwerte,
          fehlergrenze: mp.fehlergrenze,
          messunsicherheit: mp.messunsicherheit,
          abweichung: mp.mittelwertabweichung
        }
      );
    });

    const response = await this.apiStore.bpsReferenzkraftBeurteilung(messwerte);
    if (response.ok && response.json.length > 0) {
      response.json.forEach((element, index) => {
        this.beurteilungMesspunktSetzen(ziel, index, element);
      });
    } else {
      // @todo: Hinweis/Ergebnis bereinigen
    }

    // @todo: Vorbedingungen abfragen
    // @todo: Separate Vorbedingungen für Rollensatz links und rechts
    // @todo: Raus aus Klasse Rollensätze
  }

  /**
   * @todo: document
   */
  @bind @action beurteilungAbrufenGesamt() {
    [this.datensatz.rollensatz.links, this.datensatz.rollensatz.rechts].forEach(rs => {
      this.beurteilungAbrufen(rs);
    });
  }

  @action beurteilungMesspunktSetzen(ziel: IBpsReferenzkraftRollensatz, index: number, beurteilung: IBpsReferenzkraftBeurteilungPunkt) {
    ziel.messpunkte[index].messunsicherheit = beurteilung.messunsicherheit;
    ziel.messpunkte[index].konformitaet = beurteilung.konformitaet;
    ziel.messpunkte[index].mittelwertabweichung = beurteilung.mittelwertabweichung;
  }

  @action beurteilungMesspunktLoeschen(ziel: IBpsReferenzkraftRollensatz, index: number) {
    ziel.messpunkte[index].messunsicherheit = null;
    ziel.messpunkte[index].konformitaet = null;
    ziel.messpunkte[index].mittelwertabweichung = null;
  }

  /**
   * @todo: document
   */
  beurteilungAbrufenDelayed(ziel: IBpsReferenzkraftRollensatz) {
    if (this.muAbrufTimeoutRef > 0) {
      window.clearTimeout(this.muAbrufTimeoutRef);
      this.muAbrufTimeoutRef = 0;
    }

    const validateTimeoutInMs = 400;
    this.muAbrufTimeoutRef = window.setTimeout(
      () => {
        this.beurteilungAbrufen(ziel);
      },
      validateTimeoutInMs
    );
  }

  /** Ende Beurteilungen abrufen */


  /**
   * Berechnet die Mittelwerte für die Betrachtung des Rollendurchmessers
   * @note Ruft im Bedarfsfall über das Back-End neue Sollwerte ab
   */
  berechneMittelwerteRolle() {
    const { datensatz, kalibrierGegenstand } = this;
    ['links', 'rechts'].map(position => {
      let oldVal = position === 'links' ? datensatz.rollendurchmesser.mittelwert.links : datensatz.rollendurchmesser.mittelwert.rechts;
      let xstart = position === 'links' ? 0 : 2;
      let xende = position === 'links' ? 1 : 3;
      let sum: number = 0;
      let count: number = 0;
      for (let x = xstart; x <= xende; x++) {
        for (let y = 0; y < 3; y++) {
          let messwert = datensatz.rollendurchmesser.messwerte[x][y];
          if (messwert !== undefined && !isNaN(messwert)) {
            sum += messwert;
            count++;
          } else {
            this.setMittelwert(<BpsPositionRolle> position, undefined);
          }
        }
      }
      let avg = sum / count;
      // Bei geschweißten Rollen wird der Nenndurchmesser der Rolle mit betrachtet
      if (datensatz.oberflaeche === 'geschweisst') {
        if (kalibrierGegenstand) {
          let rollendurchmesser = kalibrierGegenstand.meta('rollendurchmesser');
          if (rollendurchmesser) {
            avg = (avg + parseFloat(rollendurchmesser)) / 2;
          }
        }
      }
      // Bei Rollenmessung über den Umfang, durch PI teilen
      if (datensatz.messart === 'umfang') {
        avg /= Math.PI;
      }

      this.setMittelwert(<BpsPositionRolle> position, round(avg, 1));

      let newVal = position === 'links' ? datensatz.rollendurchmesser.mittelwert.links : datensatz.rollendurchmesser.mittelwert.rechts;
      if (oldVal !== newVal) {
        this.messpunkteAktualisieren();
      }
    });
  }

  /**
   * Ermittelt das zu nutzende Kraftmessgerät aus den im Vorgang gewählten Arbeitsmitteln
   */
  kraftMessGeraet() {
    if (!this.vorgangStore.vorgang) {
      return undefined;
    }
    for (const am of this.vorgangStore.vorgang.arbeitsmittel) {
      if (am.typ.toLowerCase() === 'bps_kmg') {
        return am;
      }
    }
    return undefined;
  }

  messbereichGueltig() {
    const { datensatz } = this;
    if ((datensatz.messbereich === 'klein' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichKlein) {
      return false;
    }
    if ((datensatz.messbereich === 'groß' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichGross) {
      return false;
    }
    return true;
  }

  @action setObjektVorlage(value: IObjektVorlage | undefined) {
    this.objektVorlage = value;
  }

  @action setKalibrierGegenstand(value: IObjekt | undefined) {
    if (!value) {
      // Standardwerte setzen, falls noch kein Bremsenprüfstand gewählt wurde
      this.kalibrierGegenstand = undefined;
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 4;
      this.datensatz.anzeige = 'analog';
      return;
    }
    this.kalibrierGegenstand = new KalibrierGegenstand(value);
    // Die min. Geschwindigkeit für die Beurteilung von Drehzahl und Schlupf hängt von der Fz.Art. des Prüfstands ab
    // 4 km/h bei reinen PKW-Prüfständen, 2 km/h für alle anderen (LKW und PKW/LKW kombiniert)
    let fzart = this.kalibrierGegenstand.meta('fzart');
    if (fzart && fzart.toLowerCase() !== 'pkw') {
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 2;
    } else {
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 4;
    }
    // Art der Anzeige speichern, diese hat Auswirkungen auf die Auswertung
    this.datensatz.anzeige = 'analog';
    let anzeigeart = this.kalibrierGegenstand.meta('anzeigeart');
    if (anzeigeart && anzeigeart.toLowerCase() === 'digital') {
      this.datensatz.anzeige = 'digital';
    }
  }

  @action setMesswert(x: number, y: number, value: number | undefined) {
    this.datensatz.rollendurchmesser.messwerte[x][y] = value;
    this.berechneMittelwerteRolle();
  }

  @action setMittelwert(position: BpsPositionRolle, value: number | undefined) {
    if (position === 'links') {
      this.datensatz.rollendurchmesser.mittelwert.links = value;
    }
    if (position === 'rechts') {
      this.datensatz.rollendurchmesser.mittelwert.rechts = value;
    }
  }

  @action setOberflaeche(value: string) {
    switch (value) {
      case 'beschichtet':
        this.datensatz.oberflaeche = 'beschichtet';
        break;
      case 'geschweisst':
        this.datensatz.oberflaeche = 'geschweisst';
        break;
      default:
        this.datensatz.oberflaeche = 'beschichtet';
    }
  }

  @action setMessart(value: string) {
    switch (value) {
      case 'durchmesser':
        this.datensatz.messart = 'durchmesser';
        break;
      case 'umfang':
        this.datensatz.messart = 'umfang';
        break;
      default:
        this.datensatz.messart = 'durchmesser';
    }
  }

  @action setMessbereich(value: string) {
    switch (value) {
      case 'klein':
        this.datensatz.messbereich = 'klein';
        break;
      case 'groß':
        this.datensatz.messbereich = 'groß';
        break;
      case 'kombiniert':
        this.datensatz.messbereich = 'kombiniert';
        break;
      default:
        this.datensatz.messbereich = undefined;
    }
  }

  @action setMessbereichKlein(value: BpsMessBereichReichweite) {
    this.datensatz.messbereichKlein = value;
  }

  @action setMessbereichGross(value: BpsMessBereichReichweite) {
    this.datensatz.messbereichGross = value;
  }

  @action setJustage(value: boolean) {
    this.justage = value;
  }

  @action setReadOnly(value: boolean) {
    this.readOnly = value;
  }

  @action setMesswerte(value: Partial<IBpsReferenzkraftRolleMessung>) {
    const { datensatz } = this;
    // JSON.parse kann an dieser Stelle nicht verwendet werden, weil Daten auch teilweise gespeichert werden können
    // Das würde dann u.U. ein Teil-Interface erzeugen, mit dem die Eingabe nicht mehr funktioniert
    if (value.version) { datensatz.version = value.version; }
    if (value.rollendurchmesser) {
      if (value.rollendurchmesser.messwerte) { datensatz.rollendurchmesser.messwerte = value.rollendurchmesser.messwerte; }
      if (value.rollendurchmesser.mittelwert) { datensatz.rollendurchmesser.mittelwert = value.rollendurchmesser.mittelwert; }
    }
    if (value.oberflaeche) { datensatz.oberflaeche = value.oberflaeche; }
    if (value.messart) { datensatz.messart = value.messart; }
    if (value.messbereich) { datensatz.messbereich = value.messbereich; }
    if (value.messbereichKlein) { datensatz.messbereichKlein = value.messbereichKlein; }
    if (value.messbereichGross) { datensatz.messbereichGross = value.messbereichGross; }
    if (value.rollensatz && value.rollensatz.links) { datensatz.rollensatz.links = value.rollensatz.links; }
    if (value.rollensatz && value.rollensatz.rechts) { datensatz.rollensatz.rechts = value.rollensatz.rechts; }
    if (value.pruefortparameter) {
      if (value.pruefortparameter.beginn) { datensatz.pruefortparameter.beginn = value.pruefortparameter.beginn; }
      if (value.pruefortparameter.ende) { datensatz.pruefortparameter.ende = value.pruefortparameter.ende; }
    }
    if (value.waegezelle) { datensatz.waegezelle = value.waegezelle; }
    if (value.drehzahlschlupf) {
      if (value.drehzahlschlupf.links) { datensatz.drehzahlschlupf.links = value.drehzahlschlupf.links; }
      if (value.drehzahlschlupf.rechts) { datensatz.drehzahlschlupf.rechts = value.drehzahlschlupf.rechts; }
    }
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/BpsReferenzkraftKalibrierungStore.ts